import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ObjectToolbarConfig,
  ToolbarActionConfig,
} from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';
import type { TablePropsWithActions } from '@/utils/table/types';

import type { AssessmentActivityRegisterFields } from '../update/tabs/activities/types';

interface Props {
  title: string;
  /** Already-formatted count display string (e.g. `(12)`) — rendered verbatim. */
  counter?: string;
  tableProps: TablePropsWithActions<AssessmentActivityRegisterFields>;
  getActiveRibbonFilters: () => FilterModal[];
  /** True when the user may delete assessment activities. */
  hasDeletePermission: boolean;
  /** True when the RCSA wizard module is enabled (adds the Delete RCSA action). */
  isWizardEnabled: boolean;
  /** True when no activity is selected — greys Delete Activity. */
  isDeleteActivityDisabled: boolean;
  /** True when no RCSA activity is selected — greys Delete RCSA. */
  isDeleteRcsaDisabled: boolean;
  onDeleteActivity: () => void;
  onDeleteRcsa: () => void;
}

/**
 * Object toolbar for the Assessment Activities register, mounted only when the
 * `object_toolbar` flag is on. Like the results register, this one has no create
 * action but selection-driven bulk deletes (Activity + optional RCSA), which the
 * shared register builder does not model — so the config is assembled inline
 * (export "Download" group, relocated ribbon "Edit Filters", and a `danger`
 * group gated by the page's permission booleans + selection state). With the
 * flag off this component never renders, so `useExportOptions` runs on exactly
 * one path (toolbar vs the legacy ExportButton).
 */
export const AssessmentActivitiesRegisterToolbar: FC<Props> = ({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
  hasDeletePermission,
  isWizardEnabled,
  isDeleteActivityDisabled,
  isDeleteRcsaDisabled,
  onDeleteActivity,
  onDeleteRcsa,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'assessmentActivities',
  });
  const [showPdfModal, setShowPdfModal] = useState(false);
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } =
    useExportOptions<AssessmentActivityRegisterFields>({
      tableProps,
      entityLabel: title,
      getActiveRibbonFilters,
      onShowCustomPdfModal,
    });

  const toolbarConfig: ObjectToolbarConfig = useMemo(() => {
    const danger: ToolbarActionConfig[] = hasDeletePermission
      ? [
          {
            id: 'delete-activity',
            label: st('delete_activity_button'),
            iconName: 'trash-01',
            disabled: isDeleteActivityDisabled,
            onClick: onDeleteActivity,
          },
          ...(isWizardEnabled
            ? [
                {
                  id: 'delete-rcsa',
                  label: st('delete_rcsa_button'),
                  iconName: 'trash-01',
                  disabled: isDeleteRcsaDisabled,
                  onClick: onDeleteRcsa,
                } satisfies ToolbarActionConfig,
              ]
            : []),
        ]
      : [];

    return {
      header: { title, counter },
      actions: {
        ...(ribbonConfig
          ? {
              overflow: [
                {
                  id: 'edit',
                  label: t('edit'),
                  actions: [
                    {
                      id: 'edit-ribbon',
                      label: t('objectToolbar.filterSettings'),
                      iconName: 'filter-lines',
                      onClick: ribbonConfig.openEditRibbon,
                    },
                  ],
                },
              ],
            }
          : {}),
        export: [
          {
            id: 'export',
            actions: exportOptions.map((option) => ({
              id: option.id,
              label: option.text,
              iconName: 'download-01' as const,
              onClick: option.exportFunction,
              disabled: option.disabled,
            })),
          },
        ],
        ...(danger.length > 0 ? { danger } : {}),
      },
    };
  }, [
    title,
    counter,
    exportOptions,
    ribbonConfig,
    hasDeletePermission,
    isWizardEnabled,
    isDeleteActivityDisabled,
    isDeleteRcsaDisabled,
    onDeleteActivity,
    onDeleteRcsa,
    t,
    st,
  ]);

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
